// GENERATED by scripts/codegen-prototype/generate.mjs — DO NOT EDIT.
// Source of truth: descriptors.spaces.mjs
// Family: spaces — Space management (the space-manager app) — UI_AS_APPS_SPEC §5.

import { invoke } from '../catalog';

const withTry = <Args extends unknown[], R, E extends string>(
  fn: (...args: Args) => Promise<R>,
  fallback: E,
): ((...args: Args) => Promise<R>) & { try: (...args: Args) => Promise<{ ok: true; value: R } | { ok: false; code: E }> } =>
  Object.assign(fn, {
    try: async (...args: Args) => {
      try {
        return { ok: true as const, value: await fn(...args) };
      } catch (e) {
        return { ok: false as const, code: (e as { code?: E }).code ?? fallback };
      }
    },
  });

/** A collaborator's role on a shared space: full `owner`, read-write `writer`, or read-only `reader`. */
export type Role =
  | 'owner'
  | 'writer'
  | 'reader';

/** Summary of a space, as returned by {@link listSpaces}. */
export interface SpaceInfo {
  spaceId: string;
  role?: Role;
  owner?: string;
  name?: string;
}

/** A member of a space (for the share/manage UI). */
export interface Member {
  /** The **grantee** — `user:{uid}` | `group:{gid}`. This is the canonical name (core_concepts §4: "principal" is reserved for the authority context; a space member is a *grantee*). The host populates this on every member row. */
  grantee: string;
  /** @deprecated Use {@link Member.grantee}. Kept as an alias (same value) for back-compat during the `principal`→`grantee` migration; will be removed in a future major. The host still populates both. */
  principal: string;
  role: Role;
  login?: string;
  avatarUrl?: string;
}

/** A handle resolved to a principal (handle → who). */
export interface ResolvedUser {
  uid: string;
  login: string;
  avatarUrl?: string;
}

/** One durable grant an app holds, for the §8.11 capability audit view. */
export interface GrantRecord {
  /** The app's provider-qualified **program** identity (AA-01 `appKey`). The DEFAULT program keys to the bare `provider__namespace__repository`; a NAMED mini-app appends a fourth `enc()`-escaped component (`provider__namespace__repository__name`) so its grants isolate from the repo's other programs. Host-supplied — the app never builds this key. */
  appKey: string;
  spaceId: string;
  /** Universal mount id (§3.5). */
  mountId: string;
  subtree?: string;
  mode: 'ro' | 'rw';
  name?: string;
}

/** A pending invitation to a space (pull-based sharing, FILE_SHARING_SPEC §6.4). It grants NO access until accepted — the recipient accepts it from their inbox ({@link listMyInvites} → {@link acceptInvite}), materializing membership. The display fields (`name`/`login`/`avatarUrl`) are untrusted for rendering. */
export interface Invite {
  spaceId: string;
  /** The invitee's uid — carried so the owner's pending list can {@link revokeInvite}(spaceId, uid). */
  uid: string;
  role: Role;
  owner: string;
  name?: string;
  invitedBy: string;
  /** epoch ms (server-stamped); absent until the write settles. */
  invitedAt?: number;
  login?: string;
  avatarUrl?: string;
}

export type ListSpacesError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * List spaces you can access — all of them, or just those bound to this app.
 *
 * Capability: `spaces:app`. Catalog name: `spaces:list`.
 * @throws `Error & { code: ListSpacesError }` on host refusal.
 */
export const listSpaces = withTry<[opts?: { app?: boolean }], SpaceInfo[], ListSpacesError>(
  (opts: { app?: boolean } = {}): Promise<SpaceInfo[]> =>
    invoke<SpaceInfo[]>("spaces:list", opts),
  'unknown',
);

export type ListAllSpacesError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Enumerate ALL the user's spaces (not just this app's).
 *
 * Capability: `spaces:user`. Catalog name: `spaces:listAll`.
 * @throws `Error & { code: ListAllSpacesError }` on host refusal.
 */
export const listAllSpaces = withTry<[], SpaceInfo[], ListAllSpacesError>(
  (): Promise<SpaceInfo[]> =>
    invoke<SpaceInfo[]>("spaces:listAll", {}),
  'unknown',
);

export type GetSpaceMembersError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Read a space's members one-shot.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:members`.
 * @throws `Error & { code: GetSpaceMembersError }` on host refusal.
 */
export const getSpaceMembers = withTry<[string], Member[], GetSpaceMembersError>(
  (spaceId: string): Promise<Member[]> =>
    invoke<Member[]>("spaces:members", { spaceId }),
  'unknown',
);

export type InviteToSpaceError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown' | 'quota-exceeded';

/**
 * Invite a user (by provider handle) to a space at a role. The host resolves
 * the handle, so the app never sees other users' uids except the one it
 * invited. Pull-based (FILE_SHARING_SPEC §6.4): this writes an INVITATION,
 * not membership — the recipient must {@link acceptInvite}. Re-inviting an
 * already-invited/member user is idempotent.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:invite`.
 * @throws `Error & { code: InviteToSpaceError }` on host refusal.
 */
export const inviteToSpace = withTry<[string, string, Role], void, InviteToSpaceError>(
  async (spaceId: string, login: string, role: Role): Promise<void> => { await invoke<void>("spaces:invite", { spaceId, login, role }); },
  'unknown',
);

export type UnshareSpaceError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown' | 'conflict';

/**
 * Remove a member from a space. Refused if it would orphan the space
 * (owner-lockout, T41).
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:unshare`.
 * @throws `Error & { code: UnshareSpaceError }` on host refusal.
 */
export const unshareSpace = withTry<[string, string], void, UnshareSpaceError>(
  async (spaceId: string, uid: string): Promise<void> => { await invoke<void>("spaces:unshare", { spaceId, uid }); },
  'unknown',
);

export type SetSpaceRoleError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown' | 'conflict';

/**
 * Change a member's role. Refused if it would drop the sole owner
 * (owner-lockout, T41).
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:setRole`.
 * @throws `Error & { code: SetSpaceRoleError }` on host refusal.
 */
export const setSpaceRole = withTry<[string, string, Role], void, SetSpaceRoleError>(
  async (spaceId: string, uid: string, role: Role): Promise<void> => { await invoke<void>("spaces:setRole", { spaceId, uid, role }); },
  'unknown',
);

export type LookupUserError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Resolve a provider handle to a principal (for the invite flow).
 * Rate-limited host-side.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:lookupUser`.
 * @throws `Error & { code: LookupUserError }` on host refusal.
 */
export const lookupUser = withTry<[string], ResolvedUser, LookupUserError>(
  (login: string): Promise<ResolvedUser> =>
    invoke<ResolvedUser>("spaces:lookupUser", { login }),
  'unknown',
);

export type ListGrantsError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Enumerate every (app, mount) grant the user holds — the audit view
 * (§8.11). Elevated.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:grants`.
 * @throws `Error & { code: ListGrantsError }` on host refusal.
 */
export const listGrants = withTry<[], GrantRecord[], ListGrantsError>(
  (): Promise<GrantRecord[]> =>
    invoke<GrantRecord[]>("spaces:grants", {}),
  'unknown',
);

export type RevokeGrantError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Revoke one app's grant on a space — durable (the app can't re-mount) plus
 * a best-effort live teardown. Elevated.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:revokeGrant`.
 * @throws `Error & { code: RevokeGrantError }` on host refusal.
 */
export const revokeGrant = withTry<[string, string], void, RevokeGrantError>(
  async (appKey: string, spaceId: string): Promise<void> => { await invoke<void>("spaces:revokeGrant", { appKey, spaceId }); },
  'unknown',
);

export type ListPendingInvitesError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * The owner's outstanding invitations for a space.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:pendingInvites`.
 * @throws `Error & { code: ListPendingInvitesError }` on host refusal.
 */
export const listPendingInvites = withTry<[string], Invite[], ListPendingInvitesError>(
  (spaceId: string): Promise<Invite[]> =>
    invoke<Invite[]>("spaces:pendingInvites", { spaceId }),
  'unknown',
);

export type RevokeInviteError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Withdraw a pending invitation (distinct from {@link unshareSpace}, which
 * removes an ACCEPTED member).
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:revokeInvite`.
 * @throws `Error & { code: RevokeInviteError }` on host refusal.
 */
export const revokeInvite = withTry<[string, string], void, RevokeInviteError>(
  async (spaceId: string, uid: string): Promise<void> => { await invoke<void>("spaces:revokeInvite", { spaceId, uid }); },
  'unknown',
);

export type ListMyInvitesError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * The caller's OWN invitation inbox.
 *
 * Capability: `spaces:user`. Catalog name: `spaces:listInvites`.
 * @throws `Error & { code: ListMyInvitesError }` on host refusal.
 */
export const listMyInvites = withTry<[], Invite[], ListMyInvitesError>(
  (): Promise<Invite[]> =>
    invoke<Invite[]>("spaces:listInvites", {}),
  'unknown',
);

export type AcceptInviteError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Accept an invitation: materialize your membership at the invited role and
 * clear the invite. An invitation the caller doesn't hold rejects with
 * `forbidden` (indistinguishable from a nonexistent space; no existence
 * oracle).
 *
 * Capability: `spaces:user`. Catalog name: `spaces:acceptInvite`.
 * @throws `Error & { code: AcceptInviteError }` on host refusal.
 */
export const acceptInvite = withTry<[string], void, AcceptInviteError>(
  async (spaceId: string): Promise<void> => { await invoke<void>("spaces:acceptInvite", { spaceId }); },
  'unknown',
);

export type DeclineInviteError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Decline (dismiss) an invitation from your inbox; writes no membership.
 *
 * Capability: `spaces:user`. Catalog name: `spaces:declineInvite`.
 * @throws `Error & { code: DeclineInviteError }` on host refusal.
 */
export const declineInvite = withTry<[string], void, DeclineInviteError>(
  async (spaceId: string): Promise<void> => { await invoke<void>("spaces:declineInvite", { spaceId }); },
  'unknown',
);

export type DeleteSpaceError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Soft-delete a space you own (FILE_SHARING_SPEC §6.2): sets the tombstone,
 * so every member’s live mount falls within one snapshot and the space lists
 * nowhere. Membership is retained — undo it with {@link restoreSpace} from
 * the surface that just deleted (there is no "recently deleted" list). Not a
 * hard delete: the space’s content is not destroyed by this call.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:delete`.
 * @throws `Error & { code: DeleteSpaceError }` on host refusal.
 */
export const deleteSpace = withTry<[string], void, DeleteSpaceError>(
  async (spaceId: string): Promise<void> => { await invoke<void>("spaces:delete", { spaceId, confirm: true }); },
  'unknown',
);

export type RestoreSpaceError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Undo a {@link deleteSpace}: clears the §6.2 tombstone, and every retained
 * member’s access returns with no re-share fan-out. Reachable only while you
 * still hold the space id — the deleting surface does.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:restore`.
 * @throws `Error & { code: RestoreSpaceError }` on host refusal.
 */
export const restoreSpace = withTry<[string], void, RestoreSpaceError>(
  async (spaceId: string): Promise<void> => { await invoke<void>("spaces:restore", { spaceId, confirm: true }); },
  'unknown',
);

export type LeaveSpaceError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown' | 'owner-lockout';

/**
 * Remove YOURSELF from a space (FILE_SHARING_SPEC §9.7): your membership and
 * flattened access go in one batch, and live mounts fall through the same
 * watch as an unshare. The anchor owner cannot leave — they {@link
 * deleteSpace} instead — and a leave that would drop the last owner is
 * refused; both surface as `owner-lockout`.
 *
 * Capability: `spaces:user`. Catalog name: `spaces:leave`.
 * @throws `Error & { code: LeaveSpaceError }` on host refusal.
 */
export const leaveSpace = withTry<[string], void, LeaveSpaceError>(
  async (spaceId: string): Promise<void> => { await invoke<void>("spaces:leave", { spaceId, confirm: true }); },
  'unknown',
);

export type RenameSpaceError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown' | 'invalid-params';

/**
 * Rename a space you own (FILE_SHARING_SPEC §9.7). The name is trimmed
 * host-side and bounded (non-empty, at most the platform
 * MAX_SPACE_NAME_LENGTH); an over-long or empty name fails validation with
 * `invalid-params` before any write.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:rename`.
 * @throws `Error & { code: RenameSpaceError }` on host refusal.
 */
export const renameSpace = withTry<[string, string], void, RenameSpaceError>(
  async (spaceId: string, name: string): Promise<void> => { await invoke<void>("spaces:rename", { spaceId, name }); },
  'unknown',
);

export type ConvertSpaceToSharedError =
  'auth-required' | 'cancelled' | 'forbidden' | 'not-found' | 'unsupported-scheme' | 'unknown';

/**
 * Convert a personal space to shared (R3-259) — the deliberate, ONE-WAY
 * conversion. Only the owner may call it; the irreversibility disclosure is
 * host-drawn chrome. Converting an already-shared space is a no-op.
 *
 * Capability: `spaces:admin`. Catalog name: `spaces:convertToShared`.
 * @throws `Error & { code: ConvertSpaceToSharedError }` on host refusal.
 */
export const convertSpaceToShared = withTry<[string], void, ConvertSpaceToSharedError>(
  async (spaceId: string): Promise<void> => { await invoke<void>("spaces:convertToShared", { spaceId, confirm: true }); },
  'unknown',
);

